import React, { useState } from 'react';
import {
  Link2,
  Mail,
  Users,
  ShieldCheck,
  Globe,
  CheckCircle2,
  Calendar,
  Sparkles,
  UserPlus,
  LayoutGrid,
  Utensils,
  SlidersHorizontal,
  X,
  Copy,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';

export type GuestInviteMode = 'link' | 'email' | 'detailed';
export type LinkAccessType = 'open' | 'registered';
export type EmailDispatchMode = 'immediate' | 'draft';

export interface CelebrationGuestsCardProps {
  mode: GuestInviteMode;
  onModeChange: (mode: GuestInviteMode) => void;
  linkAccess: LinkAccessType;
  onLinkAccessChange: (access: LinkAccessType) => void;
  rsvpDeadline: string;
  onRsvpDeadlineChange: (deadline: string) => void;
  allowPlusOnes: boolean;
  onAllowPlusOnesChange: (allow: boolean) => void;
  plusOnesLimit: number;
  onPlusOnesLimitChange: (limit: number) => void;
  guestEmails: string[];
  onGuestEmailsChange: (emails: string[]) => void;
  emailDispatchMode: EmailDispatchMode;
  onEmailDispatchModeChange: (mode: EmailDispatchMode) => void;
  guestCountEstimate?: number;
  onGuestCountEstimateChange: (count: number | undefined) => void;
  trackDietary: boolean;
  onTrackDietaryChange: (track: boolean) => void;
  seatingLayoutEnabled: boolean;
  onSeatingLayoutEnabledChange: (enabled: boolean) => void;
  groupTagsEnabled: boolean;
  onGroupTagsEnabledChange: (enabled: boolean) => void;
  eventSlugPreview?: string;
  previewToken?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const CelebrationGuestsCard: React.FC<CelebrationGuestsCardProps> = ({
  mode,
  onModeChange,
  linkAccess,
  onLinkAccessChange,
  rsvpDeadline,
  onRsvpDeadlineChange,
  allowPlusOnes,
  onAllowPlusOnesChange,
  plusOnesLimit,
  onPlusOnesLimitChange,
  guestEmails,
  onGuestEmailsChange,
  emailDispatchMode,
  onEmailDispatchModeChange,
  guestCountEstimate,
  onGuestCountEstimateChange,
  trackDietary,
  onTrackDietaryChange,
  seatingLayoutEnabled,
  onSeatingLayoutEnabledChange,
  groupTagsEnabled,
  onGroupTagsEnabledChange,
  eventSlugPreview = 'sarahs-birthday-2026',
  previewToken = 'sample_token_a1b2',
}) => {
  const [rawEmailInput, setRawEmailInput] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);
  const fullInviteUrl = `https://celebrationmaster.com/g/${eventSlugPreview}?token=${previewToken}`;

  const handleEmailInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      if (e.key === ',') e.preventDefault();
      addEmailsFromInput();
    }
  };

  const addEmailsFromInput = () => {
    if (!rawEmailInput.trim()) return;
    const extracted = rawEmailInput
      .split(/[\n,; ]+/)
      .map((item) => item.trim().toLowerCase())
      .filter((item) => EMAIL_REGEX.test(item));

    if (extracted.length > 0) {
      const merged = Array.from(new Set([...guestEmails, ...extracted]));
      onGuestEmailsChange(merged);
      setRawEmailInput('');
    }
  };

  const removeEmail = (emailToRemove: string) => {
    onGuestEmailsChange(guestEmails.filter((email) => email !== emailToRemove));
  };

  return (
    <Card className="border-primary/20 shadow-sm overflow-hidden">
      <CardHeader className="pb-3 bg-gradient-to-r from-primary/5 via-primary/[0.02] to-transparent">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Guests &amp; Invitations
            </CardTitle>
            <CardDescription>
              Choose how you want to invite guests and coordinate RSVPs for this celebration.
            </CardDescription>
          </div>
          <Badge variant="outline" className="text-xs bg-white text-muted-foreground hidden sm:inline-flex">
            Guest Setup
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="p-5 space-y-6">
        {/* ─── 3 Primary Option Cards ─── */}
        <div className="grid gap-3 sm:grid-cols-3">
          {/* Option 1: Invite by Link */}
          <div
            onClick={() => onModeChange('link')}
            role="button"
            tabIndex={0}
            className={`p-4 rounded-xl border-2 transition-all cursor-pointer text-left relative flex flex-col justify-between ${
              mode === 'link'
                ? 'border-primary bg-primary/[0.04] shadow-sm ring-1 ring-primary/30'
                : 'border-muted hover:border-primary/40 hover:bg-muted/10'
            }`}
          >
            {mode === 'link' && (
              <div className="absolute top-3 right-3 text-primary">
                <CheckCircle2 className="h-5 w-5 fill-primary text-white" />
              </div>
            )}
            <div className="space-y-2">
              <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
                <Link2 className="h-5 w-5" />
              </div>
              <div>
                <div className="font-semibold text-sm text-foreground">1. Invite by Link</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Share a personalized celebration link on WhatsApp, SMS, or Social Media.
                </p>
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-border/50 flex items-center gap-1.5 text-[11px] font-medium text-blue-700">
              <Sparkles className="h-3 w-3" />
              <span>Fastest &amp; most flexible</span>
            </div>
          </div>

          {/* Option 2: Add Guests by Email */}
          <div
            onClick={() => onModeChange('email')}
            role="button"
            tabIndex={0}
            className={`p-4 rounded-xl border-2 transition-all cursor-pointer text-left relative flex flex-col justify-between ${
              mode === 'email'
                ? 'border-primary bg-primary/[0.04] shadow-sm ring-1 ring-primary/30'
                : 'border-muted hover:border-primary/40 hover:bg-muted/10'
            }`}
          >
            {mode === 'email' && (
              <div className="absolute top-3 right-3 text-primary">
                <CheckCircle2 className="h-5 w-5 fill-primary text-white" />
              </div>
            )}
            <div className="space-y-2">
              <div className="w-9 h-9 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <div className="font-semibold text-sm text-foreground">2. Add Guests by Email</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Enter or paste guest emails to dispatch personalized invitations directly.
                </p>
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-border/50 flex items-center gap-1.5 text-[11px] font-medium text-purple-700">
              <UserPlus className="h-3 w-3" />
              <span>Direct RSVP tracking</span>
            </div>
          </div>

          {/* Option 3: Detailed (Full Guest Management) */}
          <div
            onClick={() => onModeChange('detailed')}
            role="button"
            tabIndex={0}
            className={`p-4 rounded-xl border-2 transition-all cursor-pointer text-left relative flex flex-col justify-between ${
              mode === 'detailed'
                ? 'border-primary bg-primary/[0.04] shadow-sm ring-1 ring-primary/30'
                : 'border-muted hover:border-primary/40 hover:bg-muted/10'
            }`}
          >
            {mode === 'detailed' && (
              <div className="absolute top-3 right-3 text-primary">
                <CheckCircle2 className="h-5 w-5 fill-primary text-white" />
              </div>
            )}
            <div className="space-y-2">
              <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                <SlidersHorizontal className="h-5 w-5" />
              </div>
              <div>
                <div className="font-semibold text-sm text-foreground">3. Detailed (Full Suite)</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Full guest roster with seating charts, dietary preferences, and group circles.
                </p>
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-border/50 flex items-center gap-1.5 text-[11px] font-medium text-amber-700">
              <LayoutGrid className="h-3 w-3" />
              <span>Advanced coordination</span>
            </div>
          </div>
        </div>

        {/* ─── OPTION 1 DETAILS: INVITE BY LINK ─── */}
        {mode === 'link' && (
          <div className="p-4 rounded-lg bg-blue-50/50 border border-blue-100 space-y-4 animate-in fade-in duration-200">
            <div className="flex items-center justify-between pb-2 border-b border-blue-100">
              <span className="text-xs font-semibold text-blue-900 uppercase tracking-wide flex items-center gap-1.5">
                <Link2 className="h-3.5 w-3.5 text-blue-600" />
                Link Access &amp; RSVP Settings
              </span>
              <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-[11px]">
                Shareable Web Link
              </Badge>
            </div>

            {/* Sub-options: 1a vs 2a */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-800">Who can RSVP with this link?</Label>
              <div className="grid gap-3 sm:grid-cols-2">
                {/* 1a. Registration Required */}
                <label
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    linkAccess === 'registered'
                      ? 'bg-white border-blue-500 shadow-sm ring-1 ring-blue-400/40'
                      : 'bg-white/60 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="link_access"
                    value="registered"
                    checked={linkAccess === 'registered'}
                    onChange={() => onLinkAccessChange('registered')}
                    className="mt-0.5 text-primary focus:ring-primary h-4 w-4"
                  />
                  <div className="space-y-0.5">
                    <div className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                      <ShieldCheck className="h-3.5 w-3.5 text-blue-600" />
                      1a. Registration Required
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-normal">
                      Guests log in or create a quick account before submitting RSVP. Best for private, verified attendance.
                    </p>
                  </div>
                </label>

                {/* 2a. Anyone with Link */}
                <label
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    linkAccess === 'open'
                      ? 'bg-white border-blue-500 shadow-sm ring-1 ring-blue-400/40'
                      : 'bg-white/60 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="link_access"
                    value="open"
                    checked={linkAccess === 'open'}
                    onChange={() => onLinkAccessChange('open')}
                    className="mt-0.5 text-primary focus:ring-primary h-4 w-4"
                  />
                  <div className="space-y-0.5">
                    <div className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                      <Globe className="h-3.5 w-3.5 text-emerald-600" />
                      2a. Anyone with Link (Open RSVP)
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-normal">
                      Instant RSVP with just their name &amp; email. No account creation or password needed.
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Generated Link Preview */}
            <div className="p-3 bg-white rounded-md border border-blue-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="min-w-0">
                <span className="text-[11px] text-muted-foreground block">Preview of your celebration invite link:</span>
                <span className="text-xs font-mono font-medium text-blue-700 truncate block">
                  {fullInviteUrl}
                </span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1 border-blue-200 text-blue-700 hover:bg-blue-50"
                onClick={() => {
                  navigator.clipboard.writeText(fullInviteUrl);
                  setCopiedLink(true);
                  setTimeout(() => setCopiedLink(false), 2000);
                }}
              >
                <Copy className="h-3 w-3" />
                {copiedLink ? 'Copied' : 'Copy Sample'}
              </Button>
            </div>

            {/* RSVP Deadline & Plus-Ones Row */}
            <div className="grid gap-3 sm:grid-cols-2 pt-1">
              <div>
                <Label htmlFor="rsvp-deadline" className="text-xs flex items-center gap-1.5">
                  <Calendar className="h-3 w-3 text-muted-foreground" />
                  RSVP Deadline (Optional)
                </Label>
                <Input
                  id="rsvp-deadline"
                  type="date"
                  value={rsvpDeadline}
                  onChange={(e) => onRsvpDeadlineChange(e.target.value)}
                  className="mt-1 bg-white h-8 text-xs"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Guest Plus-Ones (+1s)</Label>
                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="link-allow-plus-ones"
                    checked={allowPlusOnes}
                    onChange={(e) => onAllowPlusOnesChange(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <Label htmlFor="link-allow-plus-ones" className="text-xs text-foreground cursor-pointer">
                    Allow guests to bring extra guests
                  </Label>
                </div>
                {allowPlusOnes && (
                  <div className="flex items-center gap-2 pt-1">
                    <span className="text-xs text-muted-foreground">Max per guest:</span>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      value={plusOnesLimit}
                      onChange={(e) => onPlusOnesLimitChange(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-16 h-7 text-xs bg-white"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ─── OPTION 2 DETAILS: ADD GUESTS BY EMAIL ─── */}
        {mode === 'email' && (
          <div className="p-4 rounded-lg bg-purple-50/50 border border-purple-100 space-y-4 animate-in fade-in duration-200">
            <div className="flex items-center justify-between pb-2 border-b border-purple-100">
              <span className="text-xs font-semibold text-purple-900 uppercase tracking-wide flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5 text-purple-600" />
                Guest Email Roster
              </span>
              <Badge variant="secondary" className="bg-purple-100 text-purple-800 text-[11px]">
                {guestEmails.length} Email{guestEmails.length === 1 ? '' : 's'} Added
              </Badge>
            </div>

            <div className="space-y-2">
              <Label htmlFor="guest-emails-input" className="text-xs font-semibold text-slate-800">
                Enter Guest Emails
              </Label>
              <div className="space-y-2">
                <Textarea
                  id="guest-emails-input"
                  rows={3}
                  value={rawEmailInput}
                  onChange={(e) => setRawEmailInput(e.target.value)}
                  onKeyDown={handleEmailInputKeyDown}
                  onBlur={addEmailsFromInput}
                  placeholder="Paste guest emails separated by comma, space, or new line (e.g. alex@example.com, sam@gmail.com)"
                  className="bg-white text-xs resize-none font-mono"
                />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Press Enter, comma, or click outside to add emails</span>
                  {rawEmailInput.trim() && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-6 text-[11px] px-2"
                      onClick={addEmailsFromInput}
                    >
                      + Add Emails
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Email Chips List */}
            {guestEmails.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-[11px] text-muted-foreground">Recognized Guest List ({guestEmails.length}):</Label>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 bg-white rounded-md border border-purple-100">
                  {guestEmails.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono bg-purple-100 text-purple-800 border border-purple-200"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => removeEmail(email)}
                        className="hover:text-purple-950 focus:outline-none"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Dispatch Options */}
            <div className="grid gap-3 sm:grid-cols-2 pt-2 border-t border-purple-100">
              <div>
                <Label className="text-xs font-semibold text-slate-800">Invitation Email Dispatch</Label>
                <div className="space-y-1.5 pt-1.5">
                  <label className="flex items-center gap-2 cursor-pointer text-xs">
                    <input
                      type="radio"
                      name="email_dispatch"
                      value="immediate"
                      checked={emailDispatchMode === 'immediate'}
                      onChange={() => onEmailDispatchModeChange('immediate')}
                      className="h-3.5 w-3.5 text-primary focus:ring-primary"
                    />
                    <span>🚀 Send invitations automatically upon launch</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-xs">
                    <input
                      type="radio"
                      name="email_dispatch"
                      value="draft"
                      checked={emailDispatchMode === 'draft'}
                      onChange={() => onEmailDispatchModeChange('draft')}
                      className="h-3.5 w-3.5 text-primary focus:ring-primary"
                    />
                    <span>📝 Save as draft (review before sending)</span>
                  </label>
                </div>
              </div>

              <div>
                <Label htmlFor="email-rsvp-deadline" className="text-xs">RSVP Deadline</Label>
                <Input
                  id="email-rsvp-deadline"
                  type="date"
                  value={rsvpDeadline}
                  onChange={(e) => onRsvpDeadlineChange(e.target.value)}
                  className="mt-1 bg-white h-8 text-xs"
                />
              </div>
            </div>
          </div>
        )}

        {/* ─── OPTION 3 DETAILS: DETAILED (FULL GUEST MANAGEMENT) ─── */}
        {mode === 'detailed' && (
          <div className="p-4 rounded-lg bg-amber-50/50 border border-amber-100 space-y-4 animate-in fade-in duration-200">
            <div className="flex items-center justify-between pb-2 border-b border-amber-100">
              <span className="text-xs font-semibold text-amber-900 uppercase tracking-wide flex items-center gap-1.5">
                <SlidersHorizontal className="h-3.5 w-3.5 text-amber-600" />
                Full Management Suite Configuration
              </span>
              <Badge variant="secondary" className="bg-amber-100 text-amber-800 text-[11px]">
                Full Workspace Tools
              </Badge>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {/* Estimated Guests */}
              <div>
                <Label htmlFor="guest-estimate" className="text-xs font-semibold text-slate-800">
                  Estimated Total Guests
                </Label>
                <Input
                  id="guest-estimate"
                  type="number"
                  min={1}
                  placeholder="e.g. 50"
                  value={guestCountEstimate ?? ''}
                  onChange={(e) =>
                    onGuestCountEstimateChange(e.target.value ? parseInt(e.target.value, 10) : undefined)
                  }
                  className="mt-1 bg-white h-8 text-xs"
                />
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Helps AI calibrate seating capacity, catering budgets, and supplies.
                </p>
              </div>

              {/* RSVP Deadline */}
              <div>
                <Label htmlFor="detailed-rsvp-deadline" className="text-xs font-semibold text-slate-800">
                  RSVP Deadline
                </Label>
                <Input
                  id="detailed-rsvp-deadline"
                  type="date"
                  value={rsvpDeadline}
                  onChange={(e) => onRsvpDeadlineChange(e.target.value)}
                  className="mt-1 bg-white h-8 text-xs"
                />
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Guests will be reminded before this cut-off date.
                </p>
              </div>
            </div>

            {/* Feature Modules Enablement */}
            <div className="space-y-2 pt-2 border-t border-amber-100">
              <Label className="text-xs font-semibold text-slate-800">Enable Guest Coordination Modules</Label>
              <div className="grid gap-2 sm:grid-cols-3">
                {/* Module 1: Seating Layout */}
                <label className="flex items-start gap-2.5 p-2.5 rounded-md bg-white border border-amber-200 cursor-pointer hover:border-amber-400 transition-colors">
                  <input
                    type="checkbox"
                    checked={seatingLayoutEnabled}
                    onChange={(e) => onSeatingLayoutEnabledChange(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded text-primary focus:ring-primary"
                  />
                  <div>
                    <div className="text-xs font-medium text-foreground flex items-center gap-1">
                      <LayoutGrid className="h-3.5 w-3.5 text-amber-600" />
                      Seating Layout
                    </div>
                    <p className="text-[10px] text-muted-foreground">Tables &amp; room seating board</p>
                  </div>
                </label>

                {/* Module 2: Dietary & Meal Tracking */}
                <label className="flex items-start gap-2.5 p-2.5 rounded-md bg-white border border-amber-200 cursor-pointer hover:border-amber-400 transition-colors">
                  <input
                    type="checkbox"
                    checked={trackDietary}
                    onChange={(e) => onTrackDietaryChange(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded text-primary focus:ring-primary"
                  />
                  <div>
                    <div className="text-xs font-medium text-foreground flex items-center gap-1">
                      <Utensils className="h-3.5 w-3.5 text-rose-600" />
                      Dietary &amp; Meals
                    </div>
                    <p className="text-[10px] text-muted-foreground">Allergies, vegan, kosher preferences</p>
                  </div>
                </label>

                {/* Module 3: Group Circles */}
                <label className="flex items-start gap-2.5 p-2.5 rounded-md bg-white border border-amber-200 cursor-pointer hover:border-amber-400 transition-colors">
                  <input
                    type="checkbox"
                    checked={groupTagsEnabled}
                    onChange={(e) => onGroupTagsEnabledChange(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded text-primary focus:ring-primary"
                  />
                  <div>
                    <div className="text-xs font-medium text-foreground flex items-center gap-1">
                      <Users className="h-3.5 w-3.5 text-blue-600" />
                      Groups &amp; Tags
                    </div>
                    <p className="text-[10px] text-muted-foreground">VIP, Family, College friends tags</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Note about Workspace Capabilities */}
            <div className="p-2.5 bg-amber-100/60 rounded-md text-[11px] text-amber-900 flex items-start gap-2">
              <Info className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <span>
                You can import your Google/Outlook contacts, upload CSV rosters, and customize invitation email templates once your celebration workspace is created.
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
